// context/AuthContext.js
import React, { createContext, useContext, useState, useEffect, useMemo, useRef } from "react";
import { sessionManager } from "../utils/sessionManager"; // ✅ Import session manager
import { setClientIconUrl } from "../utils/clientIcon";
import { clearProfileCache } from "../hooks/useProfile";
import { CANONICAL_PAGES, normalizeModuleKey, moduleKeyEquals, isModuleEnabledForClientRow } from "../config/moduleKeys";
import { clearAuthStorage } from "../utils/authStorage";
import { SESSION_END_REASONS, sessionEndReasonFromResponse, rememberLogoutNotice } from "../utils/authErrors";
import { reportRequestFailure, reportResponse, waitForReachable } from "../utils/connectionStatus";
import { ConnectionError } from "../utils/connectionError";

let refreshPromise = null;
// Why the last refresh returned no token: true = the server could not be
// reached (keep waiting), false = it rejected the refresh token (logged out).
let lastRefreshUnavailable = false;

const isDev = process.env.NODE_ENV !== 'production';

const AuthContext = createContext();
const BASE_URL = process.env.REACT_APP_BASE_URL;

// The single place session storage is torn down lives in utils/authStorage.js
// (every logout path uses it). Re-exported here for existing importers.
export { clearAuthStorage };

const normalizeAdminLevelData = (adminLevelData) => {
  if (!adminLevelData || typeof adminLevelData !== "object") {
    return { modules: [], features: {}, pages: null, hiddenColumns: null };
  }

  const normalizeKey = (value) => String(value || "").trim().toLowerCase();
  const normalizeFeatureValue = (value) => {
    const normalizeItem = (item) => String(item || "").trim().toLowerCase();

    if (Array.isArray(value)) {
      return value.map(normalizeItem).filter(Boolean);
    }
    if (value && typeof value === "object") {
      if (Array.isArray(value.features)) {
        return value.features.map(normalizeItem).filter(Boolean);
      }
      return Object.entries(value)
        .filter(([, enabled]) => enabled)
        .map(([featureName]) => normalizeItem(featureName))
        .filter(Boolean);
    }
    if (typeof value === "string") {
      return [normalizeItem(value)];
    }
    return [];
  };

  const modules = Array.isArray(adminLevelData.modules)
    ? adminLevelData.modules
        .map((item) => {
          if (typeof item === "string") return item;
          if (item?.module) return item.module;
          if (item?.moduleName) return item.moduleName;
          return null;
        })
        .filter(Boolean)
        .map(normalizeKey)
    : Array.isArray(adminLevelData.modulePermissions)
    ? adminLevelData.modulePermissions
        .filter((mp) => mp && mp.isEnabled !== false)
        .map((mp) => mp.module || mp.moduleName)
        .filter(Boolean)
        .map(normalizeKey)
    : [];

  let features = {};

  if (
    adminLevelData.features &&
    typeof adminLevelData.features === "object" &&
    !Array.isArray(adminLevelData.features)
  ) {
    features = Object.entries(adminLevelData.features).reduce((acc, [key, value]) => {
      const normalizedKey = normalizeKey(key);
      const normalizedValue = normalizeFeatureValue(value);
      if (normalizedValue.length) {
        acc[normalizedKey] = normalizedValue;
      }
      return acc;
    }, {});
  } else if (Array.isArray(adminLevelData.modulePermissions)) {
    features = adminLevelData.modulePermissions.reduce((acc, mp) => {
      if (!mp || mp.isEnabled === false) return acc;
      const moduleName = normalizeKey(mp.module || mp.moduleName);
      if (!moduleName) return acc;
      const enabledFeatures = Array.isArray(mp.featurePermissions)
        ? mp.featurePermissions
            .filter((fp) => fp && fp.isEnabled !== false)
            .map((fp) => normalizeKey(fp.featureName || fp.feature || fp.name))
            .filter(Boolean)
        : [];
      if (enabledFeatures.length) {
        acc[moduleName] = enabledFeatures;
      }
      return acc;
    }, {});
  }

  // ✅ v2 payloads: three-state `pages` map (absent key = HIDDEN) and
  // `hiddenColumns` map. Keys are normalized to canonical spellings so
  // consumers can look up with any legacy alias. v1 payloads simply lack
  // these fields — `null` signals "fall back to modules[] semantics".
  let pages = null;
  if (
    adminLevelData.pages &&
    typeof adminLevelData.pages === "object" &&
    !Array.isArray(adminLevelData.pages)
  ) {
    pages = Object.entries(adminLevelData.pages).reduce((acc, [key, state]) => {
      if (typeof state === "string" && state) {
        acc[normalizeModuleKey(key)] = state;
      }
      return acc;
    }, {});
  }

  let hiddenColumns = null;
  if (
    adminLevelData.hiddenColumns &&
    typeof adminLevelData.hiddenColumns === "object" &&
    !Array.isArray(adminLevelData.hiddenColumns)
  ) {
    hiddenColumns = Object.entries(adminLevelData.hiddenColumns).reduce(
      (acc, [pageKey, tables]) => {
        if (tables && typeof tables === "object" && !Array.isArray(tables)) {
          acc[normalizeModuleKey(pageKey)] = tables;
        }
        return acc;
      },
      {}
    );
  }

  return { modules, features, pages, hiddenColumns };
};

export function AuthProvider({ children }) {
  const [user, setUser] = useState(() => {
    // ✅ Priority 1: Try to get user from session manager
    const sessionUser = sessionManager.getUser();
    if (sessionUser) {
      return sessionUser;
    }

    // ✅ Priority 2: Check localStorage (persistent)
    const localUser = localStorage.getItem("user");
    if (localUser) {
      try {
        return JSON.parse(localUser);
      } catch (e) {
        console.error('Error parsing localStorage user:', e);
      }
    }

    // ✅ Priority 3: Check sessionStorage
    const sessionStorageUser = sessionStorage.getItem("user");
    if (sessionStorageUser) {
      try {
        return JSON.parse(sessionStorageUser);
      } catch (e) {
        console.error('Error parsing sessionStorage user:', e);
      }
    }

    // ✅ Priority 4: Fallback - construct from individual localStorage items.
    // Session hints only (role routing); never an invented display name —
    // the shown identity comes from the server-confirmed profile.
    const token = localStorage.getItem('token');
    if (token) {
      return {
        username: localStorage.getItem('employeeEmail') || null,
        email: localStorage.getItem('employeeEmail'),
        role: localStorage.getItem('role'),
      };
    }

    return null;
  });

  // ✅ Store permissions in app state (not localStorage)
  const [permissions, setPermissions] = useState(null);
  const [workingCompany, setWorkingCompany] = useState(() => {
    return localStorage.getItem("workingCompany") || null;
  });

  // ✅ Client-level module switches (superadmin → CompanyModulePermission).
  // One row per client, fetched once here and shared by every shell,
  // PageGuard and permission helper below, instead of each shell's own hook
  // hitting the same endpoint. `null` = not loaded (treated as "nothing to
  // enforce"); the backend is the real gate, this only hides what it denies.
  const CLIENT_MODULES_CACHE_PREFIX = "modulePermissions:";
  const [clientModules, setClientModules] = useState(null);
  const [clientModulesLoading, setClientModulesLoading] = useState(true);
  const lastClientModulesFetchRef = useRef({ at: 0, clientName: null });

  // ✅ Sync with session manager when user changes
  useEffect(() => {
    if (!user) {
      // Try to restore user from session manager
      const sessionUser = sessionManager.getUser();
      if (sessionUser) {
        setUser(sessionUser);
      }
    }
  }, [user]);

  // ✅ Resolve permissions when user changes or working company changes
  useEffect(() => {
    // Prefer admin level data attached to `user`, fall back to localStorage `adminLevel`
    let adminLevelData = null;
    if (user && user.adminLevel) {
      adminLevelData = user.adminLevel;
    } else {
      try {
        const raw = localStorage.getItem('adminLevel');
        if (raw) {
          adminLevelData = JSON.parse(raw);
        }
      } catch (e) {
        console.warn('Failed to parse localStorage.adminLevel:', e);
      }
    }

    if (adminLevelData && workingCompany) {
      // Normalize possible shapes from different endpoints
      const normalized = normalizeAdminLevelData(adminLevelData);
      const resolvedPermissions = {
        adminLevelId: adminLevelData.adminLevelId || adminLevelData.admin_level_id || adminLevelData.id || null,
        adminLevelName: adminLevelData.adminLevelName || adminLevelData.name || adminLevelData.admin_level_name || null,
        clientName: adminLevelData.clientName || adminLevelData.client || null,
        workingCompany: adminLevelData.workingCompany || adminLevelData.working_company || workingCompany,
        modules: normalized.modules,
        features: normalized.features,
        pages: normalized.pages,
        hiddenColumns: normalized.hiddenColumns,
        allModulesAllowed: adminLevelData.allModulesAllowed || false,
      };
      setPermissions(resolvedPermissions);
    } else {
      setPermissions(null);
    }
  }, [user, workingCompany]);

  // ✅ Load the client's module switches for the signed-in user and keep
  // them fresh on focus (throttled), mirroring the permissions refresh below.
  useEffect(() => {
    // A (re)run of this effect means the signed-in account changed (login,
    // logout, or a different account) — never reuse the previous account's
    // throttle window. Without this, logging out and back in within 30s
    // skipped the fetch while the session cache had already been cleared,
    // so every module looked enabled until a full page reload.
    lastClientModulesFetchRef.current = { at: 0, clientName: null };

    if (!user) {
      setClientModules(null);
      setClientModulesLoading(false);
      return undefined;
    }
    // Superadmin is the platform operator and bypasses every module switch.
    if (user.role === "superadmin") {
      setClientModules(null);
      setClientModulesLoading(false);
      return undefined;
    }
    if (!localStorage.getItem("token")) {
      setClientModulesLoading(false);
      return undefined;
    }

    // NOTE: no "cancelled" guard on the state updates below. The permissions
    // refresh further down replaces the `user` object for manager/user roles
    // right after login; if this effect re-ran on that identity change and
    // dropped the in-flight response, the throttle would then skip the
    // second fetch and the row would never load — which is exactly why the
    // effect keys on stable account fields, not on `user` itself, and why an
    // in-flight response is always applied (the provider never unmounts).
    let listening = true;
    const REFRESH_MIN_INTERVAL_MS = 30000;

    // clientName is written to localStorage at login, but a plain 'user'
    // account can reach its shell before that lands — fall back to /users/me
    // so "not cached yet" is never mistaken for "module disabled".
    const resolveClientName = async () => {
      const direct = user.clientName || localStorage.getItem("clientName");
      if (direct) return direct;
      try {
        const meRes = await apiFetch(`${BASE_URL}/users/me`, { method: "GET" });
        if (!meRes.ok) return "";
        const meJson = await meRes.json();
        const resolved = meJson?.profile?.clientName || meJson?.clientName || "";
        if (resolved) {
          try { localStorage.setItem("clientName", resolved); } catch (e) { /* private mode */ }
        }
        return resolved;
      } catch (e) {
        return "";
      }
    };

    const load = async () => {
      const clientName = await resolveClientName();
      if (!clientName) {
        setClientModulesLoading(false);
        return;
      }
      // Switches are per WORKING COMPANY (client row = default). The working
      // company is fixed on the employee record, written at login.
      const wc = String(user.workingCompany || localStorage.getItem("workingCompany") || "").trim();
      const scopeKey = `${clientName}|${wc}`;

      // Seed from the session cache so a re-mount renders the right sidebar
      // immediately instead of flashing pages that are about to be hidden.
      try {
        const raw = sessionStorage.getItem(CLIENT_MODULES_CACHE_PREFIX + scopeKey);
        if (raw) setClientModules((prev) => prev ?? JSON.parse(raw));
      } catch (e) { /* ignore cache */ }

      const now = Date.now();
      const last = lastClientModulesFetchRef.current;
      if (last.clientName === scopeKey && now - last.at < REFRESH_MIN_INTERVAL_MS) {
        setClientModulesLoading(false);
        return;
      }

      try {
        const response = await apiFetch(
          `${BASE_URL}/api/configurations/${encodeURIComponent(clientName)}` +
            (wc ? `?workingCompany=${encodeURIComponent(wc)}` : ""),
          { method: "GET" }
        );
        if (response.ok) {
          const json = await response.json();
          const data = json && json.data != null ? json.data : null;
          if (data && typeof data === "object") {
            // Throttle only a SUCCESSFUL load — a failed one must be retried.
            lastClientModulesFetchRef.current = { at: Date.now(), clientName: scopeKey };
            setClientModules(data);
            try {
              sessionStorage.setItem(CLIENT_MODULES_CACHE_PREFIX + scopeKey, JSON.stringify(data));
            } catch (e) { /* quota — cache is an optimisation only */ }
          }
        }
      } catch (e) {
        if (isDev) console.warn("Failed to load client module switches:", e);
      } finally {
        setClientModulesLoading(false);
      }
    };

    load();

    const onFocus = () => { if (listening) load(); };
    const onVisibilityChange = () => {
      if (listening && document.visibilityState === "visible") load();
    };
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onVisibilityChange);
    return () => {
      listening = false;
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onVisibilityChange);
    };
    // Keyed on the account, not the `user` object: the permissions refresh
    // below swaps `user` for a new object with the same account.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.role, user?.clientName, user?.workingCompany, user?.email, user?.username]);

  // ✅ Enhanced login function
  const login = (username, rememberMe, additionalData = {}) => {
    const userData = {
      username,
      email: additionalData.email || localStorage.getItem('employeeEmail'),
      role: additionalData.role || localStorage.getItem('role'),
      // ✅ NEW: Include admin level and permissions from JWT
      adminLevel: additionalData.adminLevel || null,
      ...additionalData
    };

    setUser(userData);

    // ✅ Store working company when user logs in
    if (additionalData.workingCompany) {
      localStorage.setItem("workingCompany", additionalData.workingCompany);
      setWorkingCompany(additionalData.workingCompany);
    }

    if (Object.prototype.hasOwnProperty.call(additionalData, "clientIconUrl")) {
      const iconValue = additionalData.clientIconUrl;
      if (iconValue) {
        localStorage.setItem("clientIconUrl", iconValue);
      } else {
        localStorage.removeItem("clientIconUrl");
      }
    }

    // ✅ Initialize session manager with user data
    sessionManager.initData(userData);

    // Store in localStorage or sessionStorage based on rememberMe
    if (rememberMe) {
      localStorage.setItem("user", JSON.stringify(userData));
      sessionStorage.removeItem("user");
    } else {
      sessionStorage.setItem("user", JSON.stringify(userData));
      localStorage.removeItem("user");
    }

    // Symmetric counterpart to "hrms:auth:logout" — lets components that
    // never unmount across a login/logout cycle within the same SPA session
    // (e.g. the Mini Chat Dock) know a fresh user just logged in and re-fetch,
    // instead of only reacting on their own next mount.
    window.dispatchEvent(new Event("hrms:auth:login"));
  };

  // ✅ Update working company and refresh permissions
  const updateWorkingCompany = (company) => {
    localStorage.setItem("workingCompany", company);
    setWorkingCompany(company);
    // Permissions will auto-resolve based on new workingCompany
  };

  // ✅ Enhanced logout function
  const logout = () => {
    clearProfileCache();
    setUser(null);
    setPermissions(null);
    setWorkingCompany(null);
    setClientModules(null);

    // ✅ Clear session manager
    sessionManager.logout();
    setClientIconUrl(null);

    // Clear every session key from storage. The JWT MUST go too — otherwise the
    // NotificationContext socket re-authenticates against the old session
    // (its useEffect only re-runs when `profile?.infinityCode` changes, and
    // `useProfile` doesn't reactively clear), the connection stays open,
    // the backend never sees a disconnect, and no `user:offline` is broadcast.
    //
    // See AUTH_STORAGE_KEYS for why the view-type and admin-level keys are in
    // scope here: leaving them behind hands the next account on this browser
    // the previous one's data scope.
    clearAuthStorage();

    // Navigation is the caller's responsibility: every caller follows this
    // with navigate("/login") (SPA). A hard window.location redirect here
    // caused the login page to reload itself right after appearing.

    // NotificationContext already listens for this to tear down its socket
    // immediately (see its own comment on `onLogout`), but nothing was ever
    // dispatching it — the listener was dead. Also used by MiniChatContext
    // to clear cached conversations/messages right away: without this,
    // components that never unmount across a login/logout cycle (the socket
    // context, the Mini Chat Dock) keep showing the previous account's data,
    // since useProfile()'s cache only reactively updates on a component's own
    // mount, not on logout/re-login within the same SPA session.
    window.dispatchEvent(new Event("hrms:auth:logout"));
  };

  // ✅ Permission checking utilities
  const normalizeKey = (value) => String(value || "").trim().toLowerCase();

  // Admin-level permissions (page states, feature grants, hidden columns)
  // exist to control ONE thing: what a promoted account sees while browsing
  // in "Admin View". They must never leak into that same account's native
  // Manager View or User View — being promoted doesn't change what a manager
  // sees as a manager. Every check below bypasses whenever the current view
  // isn't admin, regardless of role, so a manager (or user) who was granted
  // an admin level keeps their full native view untouched and only sees
  // admin-level restrictions after explicitly switching to Admin View.
  const isAdminViewActive = () => {
    const vt = localStorage.getItem("managerViewType") || localStorage.getItem("viewType") || "";
    return vt.toLowerCase() === "admin";
  };

  // ✅ Client-level module switch — evaluated FIRST in every check below.
  // A module a superadmin turned off for this client does not exist for
  // anyone in it: admin, manager, admin-level or plain user, in ANY view.
  // Only superadmin bypasses. Unknown/ungated pages (dashboard, settings)
  // and a not-yet-loaded row both count as enabled.
  const isClientModuleEnabled = (module) => {
    if (!user) return true;
    if (user.role === "superadmin") return true;
    return isModuleEnabledForClientRow(clientModules, module);
  };

  const hasModuleAccess = (module) => {
    if (!user) return false;
    if (!isClientModuleEnabled(module)) return false;
    if (user.role === "superadmin" || user.role === "admin") return true;
    if (!isAdminViewActive()) return true;
    if (permissions?.allModulesAllowed) return true;
    const normalizedModule = normalizeKey(module);
    return (permissions?.modules || []).map(normalizeKey).includes(normalizedModule);
  };

  const hasFeatureAccess = (module, feature) => {
    if (!user) return false;
    if (!isClientModuleEnabled(module)) return false;
    if (user.role === "superadmin" || user.role === "admin") return true;
    if (!isAdminViewActive()) return true;
    if (permissions?.allModulesAllowed) return true;
    const normalizedModule = normalizeKey(module);
    if (!hasModuleAccess(normalizedModule)) return false;
    const moduleFeatures = permissions?.features?.[normalizedModule] || [];
    return moduleFeatures.map(normalizeKey).includes(normalizeKey(feature));
  };

  // ✅ Three-state page visibility (v2 admin-level payloads)
  //    "VISIBLE_ACCESSIBLE"   — show nav item, allow the page
  //    "VISIBLE_ACCESS_DENIED"— show nav item (locked), block the page
  //    "HIDDEN"               — hide nav item, redirect away from the page
  const getPageState = (pageKey) => {
    if (!user) return "HIDDEN";
    // Client switch off → the page is gone for every role (hidden, not locked)
    if (!isClientModuleEnabled(pageKey)) return "HIDDEN";
    // Same bypass logic as hasModuleAccess
    if (user.role === "superadmin" || user.role === "admin") return "VISIBLE_ACCESSIBLE";
    if (!isAdminViewActive()) return "VISIBLE_ACCESSIBLE";
    if (permissions?.allModulesAllowed) return "VISIBLE_ACCESSIBLE";

    const normalized = normalizeModuleKey(pageKey);
    const pagesMap = permissions?.pages;
    if (pagesMap && typeof pagesMap === "object") {
      if (CANONICAL_PAGES.includes(normalized)) {
        return pagesMap[normalized] || "HIDDEN";
      }
      // Keys outside the canonical registry are not part of the three-state
      // system — keep modules[] semantics.
    }

    // v1 fallback: module present in modules[] → ACCESSIBLE, else HIDDEN.
    // Alias-aware so a cached legacy spelling (e.g. "solfa") still matches
    // a canonical lookup ("advancements") and vice versa.
    const inModules = (permissions?.modules || []).some((m) => moduleKeyEquals(m, pageKey));
    return inModules || hasModuleAccess(pageKey) ? "VISIBLE_ACCESSIBLE" : "HIDDEN";
  };

  // ✅ Feature visibility — thin wrapper over hasFeatureAccess (bypass = true)
  const isFeatureVisible = (pageKey, featureKey) => hasFeatureAccess(pageKey, featureKey);

  // ✅ Column visibility — columns are default-visible; only hidden when the
  // resolver's hiddenColumns map explicitly lists them. Bypassed roles see all.
  const isColumnVisible = (pageKey, tableKey, columnKey) => {
    if (!user) return true;
    if (user.role === "superadmin" || user.role === "admin") return true;
    if (!isAdminViewActive()) return true;
    if (permissions?.allModulesAllowed) return true;
    const hidden = permissions?.hiddenColumns?.[normalizeModuleKey(pageKey)]?.[tableKey];
    if (Array.isArray(hidden) && hidden.includes(columnKey)) return false;
    return true;
  };

  // ✅ Keep permissions fresh: re-resolve from the backend on mount and when
  // the tab regains focus/visibility, for non-bypassed logged-in users.
  // Without the mount-time run, a fresh page load renders from the cached
  // localStorage adminLevel until the next focus event — which looks like
  // "everything is empty until I switch apps and come back".
  // Throttled so rapid alt-tabbing never causes more than one request per
  // 30s; a company switch bypasses the throttle.
  const lastPermissionsFetchRef = useRef({ at: 0, company: null });
  useEffect(() => {
    const PERMISSIONS_REFRESH_MIN_INTERVAL_MS = 30000;

    const refreshResolvedPermissions = async () => {
      if (!user) return;
      // Bypassed roles never consult adminLevel data — nothing to refresh
      if (user.role === "superadmin" || user.role === "admin") return;
      if (!localStorage.getItem("token")) return;
      const company = workingCompany || localStorage.getItem("workingCompany");
      if (!company) return;

      const now = Date.now();
      const last = lastPermissionsFetchRef.current;
      if (last.company === company && now - last.at < PERMISSIONS_REFRESH_MIN_INTERVAL_MS) return;
      lastPermissionsFetchRef.current = { at: now, company };

      try {
        const response = await apiFetch(
          `${BASE_URL}/admin-levels/resolve/permissions?workingCompany=${encodeURIComponent(company)}`,
          { method: "GET" }
        );
        if (!response.ok) return;
        const data = await response.json();
        const fresh = data?.permissions;
        if (!fresh || typeof fresh !== "object") return;

        // Persist the same way the app stores adminLevel today so the
        // resolution effect above (user.adminLevel → localStorage.adminLevel)
        // and any next mount pick up the fresh payload.
        try {
          localStorage.setItem("adminLevel", JSON.stringify(fresh));
        } catch (e) {
          /* storage full/unavailable — state update below still applies */
        }

        setUser((prev) => {
          if (!prev) return prev;
          const updated = { ...prev, adminLevel: fresh };
          // Mirror into whichever storage currently holds the user blob
          try {
            if (localStorage.getItem("user")) {
              localStorage.setItem("user", JSON.stringify(updated));
            } else if (sessionStorage.getItem("user")) {
              sessionStorage.setItem("user", JSON.stringify(updated));
            }
          } catch (e) {
            /* non-fatal */
          }
          return updated;
        });
      } catch (e) {
        if (isDev) console.warn("Failed to refresh resolved permissions:", e);
      }
    };

    // Hydrate immediately on mount / when the session context becomes
    // available — a fresh page load must not wait for a focus event.
    refreshResolvedPermissions();

    const onFocus = () => refreshResolvedPermissions();
    const onVisibilityChange = () => {
      if (document.visibilityState === "visible") refreshResolvedPermissions();
    };

    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onVisibilityChange);
    return () => {
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onVisibilityChange);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, workingCompany]);

  // Memoized so `useAuth()` returns a STABLE object between renders.
  //
  // This was an inline object literal, which minted a new identity on every
  // render of this provider. Any consumer that put `auth` in a dependency
  // array therefore re-ran its effect on every unrelated auth render — and one
  // of them (Manager_Account's "reset to dashboard when the view type
  // changes") called setCurrentPage, so opening a page could be snapped back
  // to the dashboard a tick later by an auth refresh that had nothing to do
  // with the view.
  //
  // The four dependencies are sufficient, and that is an invariant worth
  // keeping: every function below reads only `user`, `permissions`,
  // `workingCompany` and `clientModules` from this scope. Everything else it touches is either a
  // setState identity (stable by contract) or localStorage, which is read at
  // CALL time, not captured. If a function here ever starts reading new state,
  // that state has to join this list or consumers will see a stale closure.
  const value = useMemo(
    () => ({
      user,
      login,
      logout,
      permissions,
      workingCompany,
      updateWorkingCompany,
      hasModuleAccess,
      hasFeatureAccess,
      getPageState,
      isFeatureVisible,
      isColumnVisible,
      // Client-level module switches
      clientModules,
      clientModulesLoading,
      isClientModuleEnabled,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [user, permissions, workingCompany, clientModules, clientModulesLoading],
  );

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

/**
 * Refresh access token using the refresh token from cookies
 * Returns new token on success (200), null on failure
 */
export async function refreshToken() {
  // 🔒 If refresh already in progress, wait for it
  if (refreshPromise) {
    if (isDev) console.log('⏳ Waiting for ongoing refresh...');
    return refreshPromise;
  }

  if (isDev) console.log('🔄 Starting token refresh...');

  refreshPromise = (async () => {
    lastRefreshUnavailable = false;
    try {
      const response = await fetch(`${BASE_URL}/users/refresh-token`, {
        method: 'POST',
        credentials: 'include'
      });

      // The server looked at the refresh token and rejected it: this session
      // is over.
      if (response.status === 401) {
        if (isDev) console.warn('⚠️ Refresh token invalid');
        performLogout(SESSION_END_REASONS.ENDED);
        return null;
      }

      // Anything else (5xx, gateway errors) means the server couldn't decide
      // right now — keep the session; the connection store retries.
      if (!response.ok) {
        if (isDev) console.error('❌ Refresh failed:', response.status);
        lastRefreshUnavailable = true;
        reportResponse(response);
        return null;
      }

      const data = await response.json();
      const newAccessToken = data.token;

      localStorage.setItem('token', newAccessToken);
      if (isDev) console.log('✅ Token refreshed');

      return newAccessToken;
    } catch (err) {
      // Network failure: the server never answered, so nothing says the
      // session is invalid. This used to log the user out on any blip.
      if (isDev) console.error('❌ Refresh error:', err);
      lastRefreshUnavailable = true;
      reportRequestFailure({ offline: typeof navigator !== 'undefined' && navigator.onLine === false });
      return null;
    } finally {
      // 🔓 Release semaphore
      refreshPromise = null;
    }
  })();

  return refreshPromise;
}


let logoutInProgress = false;

/**
 * End the session because the SERVER said it is over (token rejected, user
 * removed, account inactive). Does not need the auth context (apiFetch and
 * the identity store call it).
 *
 * Same teardown as the context's logout(), then a hard navigation to the
 * login page, which shows `reason` once ("session ended" / "account no
 * longer active"). Never called for network errors or 5xx.
 *
 * @param {"session_ended" | "account_inactive"} [reason]
 */
export function performLogout(reason = SESSION_END_REASONS.ENDED) {
  // Several in-flight requests can be rejected at once — tear down once.
  if (logoutInProgress) return;
  logoutInProgress = true;

  clearProfileCache();
  clearAuthStorage();
  sessionManager.logout();
  setClientIconUrl(null);
  // Stored AFTER the cleanup so the cleanup can't erase it.
  rememberLogoutNotice(reason);
  // Let long-lived listeners (notification socket, Mini Chat) drop the
  // session right away instead of waiting for the page to unload.
  window.dispatchEvent(new Event("hrms:auth:logout"));

  if (window.location.pathname.startsWith('/login')) {
    logoutInProgress = false;
    return;
  }
  window.location.href = '/login';
}

const hasUsableToken = (token) => Boolean(token) && token !== 'null' && token !== 'undefined';

const isBrowserOffline = () => typeof navigator !== 'undefined' && navigator.onLine === false;

// Read requests are safe to repeat; actions are not.
const WAITABLE_METHODS = new Set(["GET", "HEAD"]);
// Gateway / service-unavailable answers: the server is temporarily away.
const RETRYABLE_HTTP = new Set([502, 503, 504]);
// How many such answers a parked read request re-tries before giving the
// answer to the page (a lost connection — no answer at all — waits forever).
const MAX_UNAVAILABLE_RETRIES = 5;

const makeAbortError = () => {
  const err = new Error("The request was cancelled.");
  err.name = "AbortError";
  return err;
};

/**
 * One attempt: build headers, send, refresh an expired token (498) and
 * classify the answer. Never throws for a connection problem — it returns
 * `{ networkError }` or `{ response, retryable }` and lets apiFetch decide.
 * A caller-initiated abort is rethrown as is.
 */
async function attemptRequest(url, options) {
  const accessToken = localStorage.getItem("token");
  const isFormData = options.body instanceof FormData;

  // Build headers but don't force Content-Type for FormData (browser will add boundary)
  const headerViewType =
    (options.headers && (options.headers['x-view-type'] || options.headers['X-View-Type'])) ||
    null;
  const resolvedViewType =
    headerViewType ||
    localStorage.getItem('managerViewType') ||
    localStorage.getItem('viewType') ||
    'user';

  const buildHeaders = (token) => {
    const headers = {
      ...options.headers,
      'x-view-type': resolvedViewType,
      Authorization: `Bearer ${token}`,
    };
    // Ensure Accept-Language reflects current UI language (defaults to 'en')
    if (!headers['Accept-Language'] && !headers['accept-language']) {
      headers['Accept-Language'] = localStorage.getItem('language') || 'en';
    }
    if (!isFormData && !headers["Content-Type"]) {
      headers["Content-Type"] = "application/json";
    }
    return headers;
  };

  const send = (token) =>
    fetch(url, {
      ...options,
      headers: buildHeaders(token),
      credentials: "include",
    });

  let response;
  try {
    response = await send(accessToken);

    // If 498 = token expired, try to refresh token and retry
    if (response.status === 498) {
      if (isDev) console.warn('⚠️ Access token expired, attempting refresh...');
      const newAccessToken = await refreshToken();
      if (!newAccessToken) {
        // Rejected → refreshToken() already ended the session; hand back the
        // 498. Unreachable → the session is kept and this is retryable.
        return { response, retryable: lastRefreshUnavailable };
      }
      response = await send(newAccessToken);
    }
  } catch (err) {
    if (err?.name === 'AbortError') throw err;
    return { networkError: err };
  }

  // Only a request that actually carried a token can tell us the session is
  // over — an anonymous request answered with TOKEN_MISSING says nothing.
  if (hasUsableToken(accessToken)) {
    const endReason = await sessionEndReasonFromResponse(response);
    if (endReason) {
      performLogout(endReason);
      return { response, retryable: false, sessionEnded: true };
    }
  }

  return { response, retryable: RETRYABLE_HTTP.has(response.status) };
}

// Identical read requests parked during the same outage share ONE re-try
// (a widget that polls every 30s would otherwise fire dozens of copies the
// moment the connection returns). Each caller gets its own clone.
const sharedRetries = new Map();

const retryKey = (url, options) => {
  const headers = options.headers || {};
  return [
    String(options.method || "GET").toUpperCase(),
    url,
    headers['x-view-type'] || headers['X-View-Type'] || localStorage.getItem('managerViewType') || localStorage.getItem('viewType') || '',
    headers['Accept-Language'] || headers['accept-language'] || localStorage.getItem('language') || '',
  ].join(" ");
};

const followShared = (shared, signal) =>
  new Promise((resolve, reject) => {
    if (signal?.aborted) {
      reject(makeAbortError());
      return;
    }
    const onAbort = () => reject(makeAbortError());
    signal?.addEventListener('abort', onAbort, { once: true });
    shared.then(
      (response) => {
        signal?.removeEventListener('abort', onAbort);
        // The shared original is never read, so cloning it always works.
        resolve(response.clone());
      },
      (err) => {
        signal?.removeEventListener('abort', onAbort);
        reject(err);
      }
    );
  });

/**
 * Fetch API with automatic token refresh on 498 (expired access token).
 *
 * Failure handling:
 *  • 401/403 carrying a session-ending auth code (see utils/authErrors.js)
 *    → performLogout(reason). A plain 403 (feature/permission denial) is
 *    returned to the caller untouched — it never ends the session.
 *  • The server can't be reached (fetch throws, 502/503/504, or an expired
 *    token can't be refreshed because the server is away):
 *      – GET / HEAD: the request waits until the server answers again
 *        (utils/connectionStatus.js waitForReachable — the connection banner
 *        tells the user) and is then completed. Pages therefore finish
 *        loading by themselves after an outage, keeping their state.
 *      – any other method: never replayed automatically (it could run an
 *        action twice); it rejects with a bilingual ConnectionError instead
 *        of the browser's raw "Failed to fetch".
 *    The session is kept in every case.
 *
 * Extra option: `reportConnection: false` — the caller handles connection
 * problems itself (the identity probe and logout do): no waiting, and the
 * connection store is not told.
 */
export async function apiFetch(url, options = {}) {
  const { reportConnection = true, ...fetchOptions } = options;
  const method = String(fetchOptions.method || "GET").toUpperCase();
  const waitable =
    reportConnection &&
    WAITABLE_METHODS.has(method) &&
    hasUsableToken(localStorage.getItem("token"));

  const outcome = await attemptRequest(url, fetchOptions);

  if (outcome.networkError) {
    if (reportConnection) reportRequestFailure({ offline: isBrowserOffline() });
    if (!waitable) throw new ConnectionError(outcome.networkError);
  } else if (!outcome.retryable || !waitable) {
    if (reportConnection && !outcome.sessionEnded) reportResponse(outcome.response);
    return outcome.response;
  } else {
    reportRequestFailure({ offline: isBrowserOffline() });
  }

  // ── Read request, server unreachable: wait for it, then complete. ──
  const key = retryKey(url, fetchOptions);
  let shared = sharedRetries.get(key);
  if (!shared) {
    // The shared re-try must outlive any single caller, so it does not use
    // the first caller's abort signal (each caller can still stop waiting).
    const { signal: callerSignal, ...retryOptions } = fetchOptions;
    shared = (async () => {
      let unavailableAnswers = 0;
      for (;;) {
        await waitForReachable();
        const next = await attemptRequest(url, retryOptions);
        if (next.networkError) {
          // No answer at all: a real outage — keep waiting.
          reportRequestFailure({ offline: isBrowserOffline() });
          continue;
        }
        if (next.retryable) {
          unavailableAnswers += 1;
          if (unavailableAnswers < MAX_UNAVAILABLE_RETRIES) {
            reportRequestFailure({ offline: isBrowserOffline() });
            continue;
          }
        }
        // An answer (or an endpoint that keeps saying "unavailable" while the
        // server itself is reachable): hand it to the page, which then shows
        // its own state instead of loading forever.
        if (!next.sessionEnded) reportResponse(next.response);
        return next.response;
      }
    })();
    sharedRetries.set(key, shared);
    shared.then(
      () => sharedRetries.delete(key),
      () => sharedRetries.delete(key)
    );
  }
  return followShared(shared, fetchOptions.signal);
}
